import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useOrganization } from './useOrganization';
import { useOrganizationMembers } from './api/useOrganizationMembers';

export type PageName =
  | 'dashboard'
  | 'assets'
  | 'audits'
  | 'reports'
  | 'people'
  | 'locations'
  | 'categories'
  | 'departments'
  | 'custom_fields'
  | 'settings'
  | 'billing'
  | 'activity_log'
  | 'saved_views';

export interface PagePermissions {
  view: boolean;
  write: boolean;
  delete: boolean;
}

export interface UserPermissions {
  [page: string]: PagePermissions;
}

const DEFAULT_PERMISSIONS: PagePermissions = {
  view: false,
  write: false,
  delete: false,
};

export function usePermissions() {
  const { user } = useAuth();
  const { organizationId } = useOrganization();
  const { data: members } = useOrganizationMembers(organizationId);

  const { permissions, isAdmin } = useMemo<{ permissions: UserPermissions; isAdmin: boolean }>(() => {
    if (!user || !members) {
      return { permissions: {}, isAdmin: false };
    }

    // Find current user's membership
    const membership = members.find(
      (m) => m.profile?.id === user.id
    );

    if (!membership) {
      return { permissions: {}, isAdmin: false };
    }

    // Return permissions from database and admin status
    return {
      permissions: (membership.permissions as UserPermissions) || {},
      isAdmin: membership.is_admin,
    };
  }, [user, members]);

  const can = (page: PageName, action?: 'view' | 'write' | 'delete') => {
    // Admins always have full access
    if (isAdmin) return true;

    const pagePerms = permissions[page] || DEFAULT_PERMISSIONS;

    // If no action specified, check if page has view permission (can access)
    if (!action) {
      return pagePerms.view;
    }

    // Check specific action - view permission required for all actions
    return pagePerms.view && pagePerms[action];
  };

  const canAccess = (page: PageName) => {
    // Admins can access everything
    if (isAdmin) return true;

    const pagePerms = permissions[page] || DEFAULT_PERMISSIONS;
    return pagePerms.view;
  };

  const canView = (page: PageName) => can(page, 'view');
  const canWrite = (page: PageName) => can(page, 'write');
  const canDelete = (page: PageName) => can(page, 'delete');

  // Backwards compatibility helpers (deprecated)
  const canCreate = (page: PageName) => canWrite(page);
  const canRead = (page: PageName) => canView(page);
  const canUpdate = (page: PageName) => canWrite(page);

  return {
    permissions,
    isAdmin,
    can,
    canAccess,
    canView,
    canWrite,
    canDelete,
    // Backwards compatibility (deprecated)
    canCreate,
    canRead,
    canUpdate,
  };
}
